import _extends from "@babel/runtime/helpers/extends";
import "core-js/modules/es6.string.bold.js";
import React from 'react';
import { useDeprecatedPropsWarning } from '@nerdwallet/deprecate';
import { Type } from '../Type/Type';
import { TypographyContextProvider, useIsBoldType, useIsNestedType } from '../Type/TypographyContext';
import { useTextStyles } from './useTextStyles';
/**
 * Various tones that are semantic abstractions
 * on top of the underlying color system.
 *
 * The inherent benefit of a semantic abstraction
 * enables the underlying colors to change. They
 * currently (at inception of this prop, in v2.6) map to:
 *
 * <Text tone="default" /> --> @neutral-darkest
 * <Text tone="light" />  --> @neutral-darker
 * <Text tone="critical" /> --> @red-darkest
 * <Text tone="positive"  />  --> @green-darkest
 * <Text tone="assist" />  --> @blue-darkest
 * <Text tone="caution" />  --> @yellow-darkest
 */

/**
 *
 * For the given `size` and `bold` props, returns the corresponding
 * `base-styles` type size. Note that there are two Text sizes for which we
 * don't have `base-styles` sizes:
 *
 * - `large && !bold`, which is 20px on desktop and 18 px on mobile
 * - `x-small`, which is 11px on all viewports
 */
function getNumericSize(_ref) {
  var size = _ref.size,
      bold = _ref.bold;

  switch (size) {
    case 'large':
      return bold ? '3' : '2-half';

    case 'default':
      return '2';

    case 'small':
      return '1';

    default:
      return undefined;
  }
}
/**
 * Lookup map to find the correct color from the tone prop.
 */


var toneToColor = function toneToColor(tone) {
  switch (tone) {
    case 'default':
      return 'neutral-darkest';

    case 'light':
      return 'neutral-darker';

    case 'critical':
      return 'red-dark';

    case 'positive':
      return 'green-dark';

    case 'assist':
      return 'blue-dark';

    case 'caution':
      return 'yellow-dark';

    default:
      return undefined;
  }
};
/**
 * The Text component has several weight and size variants to enable levels of hierarchy within a
 * section of a page – large bold, large, default, small bold, small.
 *
 * - Text Large Bold, Text Large, and Text Small Bold make content scannable by adding texture and
 * contrast within a section of a page
 * - Text (default) displays standard body copy – the baseline size for the hierarchy
 * - Mix and match variations of Text to drive hierarchy within a section of a page
 * - Limit line-width to 8 columns per Level AA WCAG guidelines
 */


export var Text = /*#__PURE__*/React.forwardRef(function (props, ref) {
  // If we're nested within another typography component, we can rely on
  // the CSS cascade in web, or native inheritance on React Native, to handle
  // `bold` and `size` if they aren't explicitly provided.
  var isNestedType = useIsNestedType(); // We can't rely on `<Type>` handling the merging of `bold` from context
  // and the `bold` prop for us, so we need to do that manually here.

  var isBoldType = useIsBoldType();
  var align = props.align,
      _props$bold = props.bold,
      bold = _props$bold === void 0 ? isNestedType ? isBoldType : false : _props$bold,
      children = props.children,
      _props$component = props.component,
      component = _props$component === void 0 ? 'span' : _props$component,
      id = props.id,
      light = props.light,
      overflowWrap = props.overflowWrap,
      _props$size = props.size,
      size = _props$size === void 0 ? isNestedType ? undefined : 'default' : _props$size,
      tone = props.tone;
  var fontFamily;

  if (!isNestedType || bold) {
    fontFamily = bold ? 'gotham-medium' : 'gotham';
  }

  var textOverflowWrap = overflowWrap === true ? 'break-word' : overflowWrap; // TODO: remove prop and this warning in Currency v3

  useDeprecatedPropsWarning({
    component: 'Text',
    deprecatedProps: [{
      deprecated: 'light',
      version: '2.6.0',
      migration: 'Use the tone prop.'
    }],
    props: {
      light: light
    }
  });
  return /*#__PURE__*/React.createElement(Type, _extends({}, useTextStyles({
    overflowWrap: textOverflowWrap,
    size: size
  }), {
    "data-currency": "Text",
    bold: false,
    align: align,
    fontFamily: fontFamily,
    component: component,
    color: tone ? toneToColor(tone) : undefined,
    id: id,
    light: light,
    size: size ? getNumericSize({
      size: size,
      bold: bold
    }) : undefined,
    ref: ref
  }), /*#__PURE__*/React.createElement(TypographyContextProvider // We don't want to cascade the bold value through to underlying children
  // if we're already managing it within the specific font-weight requirements
  // of the "Text bold" typestyle and Gotham medium weight; we'll defer to it
  // being managed by the `gotham-medium` rule, which includes both the weight
  // and the font-family.
  , {
    bold: fontFamily === 'gotham-medium' ? false : bold
  }, /*#__PURE__*/React.createElement(TypographyContextProvider // We don't want to cascade the bold value through to underlying children
  // if we're already managing it within the specific font-weight requirements
  // of the "Text bold" typestyle and Gotham medium weight; we'll defer to it
  // being managed by the `gotham-medium` rule, which includes both the weight
  // and the font-family.
  , {
    bold: fontFamily === 'gotham-medium' ? false : bold
  }, children)));
});
Text.displayName = 'Text';